// apps/web/src/pages/planejamento/os-detalhe-panel.tsx — Fase 03, Plano 04 (OPS-10).
import React, { useState } from 'react';
import {
  X,
  Lock,
  Unlock,
  Wrench,
  Truck,
  ArrowRight,
} from 'lucide-react';
import type { OrdemServicoOps10Fields } from '@pieltec/contracts';

export interface OsDetalheData extends Partial<OrdemServicoOps10Fields> {
  id: string;
  opId: string;
  opNumero?: string;
  etapaOrdem: number;
  celulaId: string;
  celulaNome?: string;
  status: string;
  duracaoEstimadaHoras?: number;
  dataInicioProgramada?: string;
  dataFimProgramada?: string;
  predecessores?: ReadonlyArray<{ id: string; etapaOrdem: number; celulaNome: string }>;
}

interface OsDetalhePanelProps {
  os: OsDetalheData;
  onClose: () => void;
  onLiberarBloqueio?: (osId: string, justificativa: string) => Promise<void>;
  /** Renderiza só o conteúdo (sem moldura/cabeçalho/fechar), para uso dentro de outro painel (Kanban). */
  embutido?: boolean;
}

export const OsDetalhePanel: React.FC<OsDetalhePanelProps> = ({
  os,
  onClose,
  onLiberarBloqueio,
  embutido = false,
}) => {
  const [liberando, setLiberando] = useState(false);
  const [justificativa, setJustificativa] = useState('');
  const [showLiberarInput, setShowLiberarInput] = useState(false);

  const regra = os.regraBloqueioLiberacao;
  const isBloqueada = Boolean(regra && !regra.liberadoAt);

  const handleLiberar = async () => {
    if (!onLiberarBloqueio || !justificativa.trim()) return;
    setLiberando(true);
    try {
      await onLiberarBloqueio(os.id, justificativa);
      setShowLiberarInput(false);
    } finally {
      setLiberando(false);
    }
  };

  return (
    <div
      className={
        embutido
          ? 'flex flex-col'
          : 'w-80 md:w-96 bg-white border-l shadow-2xl h-full flex flex-col z-30 animate-in slide-in-from-right duration-200'
      }
    >
      {/* Header */}
      {!embutido && (
      <div className="flex items-center justify-between px-5 py-4 border-b bg-slate-50">
        <div>
          <span className="text-[11px] font-mono font-semibold text-slate-500 uppercase">
            {os.opNumero ? `OP ${os.opNumero}` : `OS ${os.id.slice(0, 8)}`}
          </span>
          <h2 className="text-base font-bold text-slate-900">
            Etapa {os.etapaOrdem}: {os.celulaNome ?? os.celulaId}
          </h2>
        </div>
        <button
          onClick={onClose}
          className="p-1 rounded-md text-slate-400 hover:text-slate-600 hover:bg-slate-200"
        >
          <X className="w-5 h-5" />
        </button>
      </div>
      )}

      {/* Conteúdo */}
      <div className={embutido ? 'space-y-4 text-xs' : 'p-5 overflow-y-auto flex-1 space-y-4 text-xs'}>
        {/* Status & Duração */}
        <div className="grid grid-cols-2 gap-2 p-3 bg-slate-50 rounded-lg border">
          <div>
            <span className="text-[11px] text-slate-500 block">Status:</span>
            <span className="font-semibold text-slate-800">{os.status}</span>
          </div>
          <div>
            <span className="text-[11px] text-slate-500 block">Duração Estimada:</span>
            <span className="font-semibold text-slate-800 font-mono">
              {os.duracaoEstimadaHoras ?? 0}h
            </span>
          </div>
        </div>

        {/* Predecessores no Grafo (OPS-10) */}
        <div className="space-y-2">
          <div className="flex items-center gap-1.5 font-semibold text-slate-800">
            <ArrowRight className="w-3.5 h-3.5 text-slate-500" />
            <span>Predecessores no Grafo (OsPrecedencia):</span>
          </div>
          {!os.predecessores || os.predecessores.length === 0 ? (
            <p className="text-slate-400 italic">Etapa raiz (sem predecessores obrigatórios).</p>
          ) : (
            <div className="space-y-1">
              {os.predecessores.map((p) => (
                <div
                  key={p.id}
                  className="flex items-center justify-between px-2.5 py-1.5 bg-slate-50 rounded border"
                >
                  <span className="font-medium text-slate-700">
                    Etapa {p.etapaOrdem}: {p.celulaNome}
                  </span>
                  <span className="text-[10px] font-mono text-slate-400">FS</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Recursos Alternativos Elegíveis (OPS-10) */}
        <div className="space-y-2">
          <div className="flex items-center gap-1.5 font-semibold text-slate-800">
            <Wrench className="w-3.5 h-3.5 text-slate-500" />
            <span>Recursos Elegíveis / Alternativos:</span>
          </div>
          {(!os.recursoAlternativoIds || os.recursoAlternativoIds.length === 0) ? (
            <p className="text-slate-400 italic">Recurso padrão da célula configurado.</p>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {os.recursoAlternativoIds.map((recId, idx) => (
                <span
                  key={recId}
                  className={`px-2 py-0.5 rounded text-[11px] font-mono border ${
                    idx === 0
                      ? 'bg-slate-900 text-white font-semibold'
                      : 'bg-slate-100 text-slate-700'
                  }`}
                >
                  {recId} {idx === 0 && '(Principal)'}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Terceirização (OPS-10) */}
        {os.terceirizado && (
          <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg space-y-1">
            <div className="flex items-center gap-1.5 text-amber-800 font-semibold">
              <Truck className="w-4 h-4 text-amber-600" />
              <span>Etapa Terceirizada</span>
            </div>
            <p className="text-[11px] text-amber-700">
              Fornecedor / Parceiro Externo:{' '}
              <strong className="font-mono">{os.fornecedorExternoId ?? 'A definir'}</strong>
            </p>
          </div>
        )}

        {/* Regra de Bloqueio / Liberação (D-16 / OPS-10) */}
        {regra && (
          <div
            className={`p-3 rounded-lg border space-y-2 ${
              isBloqueada
                ? 'bg-purple-50/70 border-purple-200 text-purple-900'
                : 'bg-emerald-50 border-emerald-200 text-emerald-900'
            }`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1.5 font-semibold">
                {isBloqueada ? (
                  <Lock className="w-4 h-4 text-purple-600" />
                ) : (
                  <Unlock className="w-4 h-4 text-emerald-600" />
                )}
                <span>
                  {isBloqueada ? 'Liberação Bloqueada (D-16)' : 'Etapa Liberada'}
                </span>
              </div>
              <span
                className={`px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase ${
                  isBloqueada ? 'bg-purple-200 text-purple-800' : 'bg-emerald-200 text-emerald-800'
                }`}
              >
                {regra.condicao}
              </span>
            </div>

            {regra.observacao && (
              <p className="text-[11px] text-purple-800">{regra.observacao}</p>
            )}

            {isBloqueada && (
              <div className="pt-2 border-t border-purple-200 space-y-2">
                {!showLiberarInput ? (
                  <button
                    onClick={() => setShowLiberarInput(true)}
                    className="w-full py-1.5 bg-purple-700 text-white rounded text-xs font-semibold hover:bg-purple-800"
                  >
                    Liberar Manualmente com Justificativa
                  </button>
                ) : (
                  <div className="space-y-2">
                    <input
                      type="text"
                      placeholder="Justificativa da liberação..."
                      value={justificativa}
                      onChange={(e) => setJustificativa(e.target.value)}
                      className="w-full px-2 py-1 text-xs border rounded bg-white text-slate-800"
                    />
                    <div className="flex gap-1.5">
                      <button
                        onClick={() => setShowLiberarInput(false)}
                        className="flex-1 py-1 border rounded text-xs text-slate-700 bg-white"
                      >
                        Cancelar
                      </button>
                      <button
                        onClick={() => void handleLiberar()}
                        disabled={liberando || !justificativa.trim()}
                        className="flex-1 py-1 bg-purple-700 text-white rounded text-xs font-semibold disabled:opacity-50"
                      >
                        {liberando ? 'Liberando...' : 'Confirmar'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {!isBloqueada && regra.liberadoManualmentePor && (
              <p className="text-[10px] text-emerald-700">
                Liberado por {regra.liberadoManualmentePor} em {regra.liberadoAt?.slice(0, 10)}.
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
